/**
 * CodeEntryForm Component
 * Form for entering redemption codes with uppercase input and validation
 */

import { useState } from 'react';
import { FormButton } from '../form/FormButton';

// Minimum expected length for redemption codes
const MIN_CODE_LENGTH = 10;

interface CodeEntryFormProps {
  onSubmit: (code: string) => void;
  initialCode?: string;
}

export function CodeEntryForm({ onSubmit, initialCode }: CodeEntryFormProps) {
  const [code, setCode] = useState(initialCode || '');
  const [error, setError] = useState('');

  // Handle input change with uppercase transformation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setCode(value);
    setError('');
  };

  // Handle paste with uppercase transformation
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').toUpperCase();
    setCode(pastedText);
    setError('');
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedCode = code.trim();

    // Basic validation
    if (!trimmedCode) {
      setError('Please enter a redemption code');
      return;
    }

    if (trimmedCode.length < MIN_CODE_LENGTH) {
      setError('Code appears to be too short');
      return;
    }

    onSubmit(trimmedCode);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Enter Redemption Code
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="code-input"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Code
          </label>
          <input
            id="code-input"
            type="text"
            value={code}
            onChange={handleInputChange}
            onPaste={handlePaste}
            placeholder="ENTER-YOUR-CODE-HERE"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!!initialCode}
          />
          {error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          {initialCode && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Code provided via URL
            </p>
          )}
        </div>

        <FormButton
          type="submit"
          isLoading={false}
          loadingText="Processing..."
          className="w-full btn-primary"
        >
          Redeem Code
        </FormButton>
      </form>
    </div>
  );
}
